import React from 'react';
import { BaseTool, IToolManifest, IToolContext } from '../interfaces/ITool';
import { BaseCommand, ICommand } from '../../commands/ICommand';
import { ToolComponentFactory } from '../components/ToolComponents';
import { Palette } from 'lucide-react';

// This class is deprecated - use ChangeNodeColorCommand instead
// Left for backward compatibility but should not be used
export class ChangeColorCommand extends BaseCommand {
  protected itemIds: string[];
  protected oldColors: Map<string, string | undefined>;
  protected newColor: string;
  protected updateItem: (id: string, color: string | undefined) => void;
  
  constructor(
    itemIds: string[],
    newColor: string,
    oldColors: Map<string, string | undefined>,
    updateItem: (id: string, color: string | undefined) => void,
    description?: string
  ) {
    super('Change Color', description || `Change color to ${newColor}`);
    console.warn('ChangeColorCommand is deprecated - use ChangeNodeColorCommand instead');
    this.itemIds = itemIds;
    this.newColor = newColor;
    this.oldColors = oldColors;
    this.updateItem = updateItem;
  }
  
  execute(): void {
    this.itemIds.forEach(id => {
      this.updateItem(id, this.newColor);
    });
  }
  
  undo(): void {
    this.itemIds.forEach(id => {
      const oldColor = this.oldColors.get(id);
      this.updateItem(id, oldColor);
    });
  }
  
  canMergeWith(other: ICommand): boolean {
    if (!(other instanceof ChangeColorCommand)) return false;
    
    const otherCommand = other as ChangeColorCommand;
    
    // Can merge if same items are being colored within time window
    return JSON.stringify(this.itemIds.sort()) === 
           JSON.stringify(otherCommand.itemIds.sort());
  }
  
  mergeWith(other: ICommand): ICommand {
    const otherCommand = other as ChangeColorCommand;
    // Keep the old colors from this command, but use new color from other
    return new ChangeColorCommand(
      this.itemIds,
      otherCommand.newColor,
      this.oldColors,
      this.updateItem
    );
  }
}

// Base color tool that can be extended
export abstract class ColorTool extends BaseTool {
  protected currentColor = '#000000';
  protected presetColors: Record<string, string> = {
    'Black': '#000000',
    'White': '#FFFFFF',
    'Red': '#FF0000',
    'Green': '#00FF00',
    'Blue': '#0000FF',
    'Yellow': '#FFFF00',
    'Cyan': '#00FFFF',
    'Magenta': '#FF00FF'
  };
  
  getBaseManifest(): Partial<IToolManifest> {
    return {
      name: 'Color',
      icon: '🎨',
      category: 'style',
      description: 'Change colors',
      version: '1.0.0',
      
      requiresSelection: true,
      minSelection: 1,
      
      ui: {
        toolbar: true,
        contextMenu: true,
        propertyPanel: true
      },
      
      capabilities: {
        modifiesShapes: true,
        createsShapes: false,
        deletesShapes: false,
        modifiesConnectors: true,
        createsConnectors: false,
        deletesConnectors: false,
        requiresUserInput: true,
        supportsMultiSelect: true,
        supportsBatchOperations: true
      },
      
      shortcuts: [{
        key: 'c',
        modifiers: ['shift'],
        description: 'Open color picker'
      }]
    };
  }
  
  canExecute(context: IToolContext): boolean {
    return context.selection.size > 0;
  }
  
  // Abstract methods that must be implemented by subclasses
  abstract getItemColor(itemId: string): string | undefined;
  abstract setItemColor(itemId: string, color: string | undefined): void;
  abstract getSelectedItems(): string[];
  
  execute(params: { color: string }): ICommand | null {
    if (!this.context || !params.color) return null;
    
    const selectedIds = this.getSelectedItems();
    if (selectedIds.length === 0) return null;
    
    // Store old colors
    const oldColors = new Map<string, string | undefined>();
    selectedIds.forEach(id => {
      oldColors.set(id, this.getItemColor(id));
    });
    
    // Update current color
    this.currentColor = params.color;
    
    return new ChangeColorCommand(
      selectedIds,
      params.color,
      oldColors,
      (id, color) => this.setItemColor(id, color)
    );
  }
  
  getToolbarComponent(): React.ComponentType<any> {
    return ({ onExecute }: any) => {
      const [color, setColor] = React.useState(this.currentColor);
      const selectedCount = this.getSelectedItems().length;
      const isDisabled = selectedCount === 0;
      
      return ToolComponentFactory.createContainer([
        // Color picker input
        React.createElement('input', {
          key: 'color-input',
          type: 'color',
          value: color,
          onChange: (e: any) => {
            const newColor = e.target.value;
            setColor(newColor);
            onExecute({ color: newColor });
          },
          disabled: isDisabled,
          className: `w-8 h-8 border border-border rounded cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${
            isDisabled ? 'bg-muted' : 'bg-background'
          }`,
          title: isDisabled ? 'Select items first' : 'Pick a color'
        }),
        
        // Presets dropdown using standardized component
        ToolComponentFactory.createSelect({
          icon: Palette,
          placeholder: 'Presets',
          onValueChange: (presetColor: string) => {
            setColor(presetColor);
            onExecute({ color: presetColor });
          },
          disabled: isDisabled,
          options: Object.entries(this.presetColors).map(([name, presetColor]) => ({
            value: presetColor,
            label: name,
            description: undefined
          }))
        })
      ]);
    };
  }
}